/**
 * Section shadowing — course staff's triage of flags to course staff: whether
 * a flag is dealt with, and staff notes on it.
 *
 * One triage doc per flag, in the per-course shadowing tree rather than next to
 * the flag, so the flag's author can never read or write it and the staff list
 * reads a course's triage with one plain collection read:
 * `sectionShadow/{courseId}/staffFlagTriage/{triageId}`, with append-only notes
 * under `.../staffFlagTriage/{triageId}/notes/{noteId}`.
 *
 * Mirrors the reports engine (`../report.ts`): a two-state status, notes as an
 * append-only subcollection of `{authorUid, authorName, text, at}`, and a
 * denormalized note count so the list needs no per-row note read.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../../types';
import type {CourseId} from '../../../types/branded';
import {
  SECTION_ANALYSIS_COLLECTION,
  SECTION_ANALYSIS_SECTION_WEEKS_SUBCOLLECTION,
  SECTION_ANALYSIS_SECTIONS_SUBCOLLECTION,
  SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';
import {SECTION_SHADOW_COLLECTION} from './sectionShadowQueue';

/** Subcollection of `sectionShadow/{courseId}` holding staff triage of flags. */
export const SECTION_SHADOW_STAFF_FLAG_TRIAGE_SUBCOLLECTION =
  'staffFlagTriage' as const;

/** Subcollection of a triage doc holding its staff notes. */
export const SHADOW_STAFF_FLAG_TRIAGE_NOTES_SUBCOLLECTION = 'notes' as const;

/**
 * Longest note body the rules accept. The rules repeat this value, so change
 * both together.
 */
export const SHADOW_STAFF_FLAG_TRIAGE_NOTE_MAX_LENGTH = 2000;

/**
 * Longest display name the rules accept on a note or a completion. The rules
 * repeat this value, so change both together.
 */
export const SHADOW_STAFF_FLAG_TRIAGE_NAME_MAX_LENGTH = 200;

/** Whether staff still owe a flag attention. Absent on a doc reads as `open`. */
export const ShadowStaffFlagTriageStatusSchema = z.enum(['open', 'complete']);

/** Whether staff still owe a flag attention. */
export type ShadowStaffFlagTriageStatus = z.infer<
  typeof ShadowStaffFlagTriageStatusSchema
>;

/**
 * Staff triage of one flag. Created on the first staff action (a note or a
 * status change); a flag with no triage doc is open with no notes.
 */
export const ShadowStaffFlagTriageDocumentSchema = z
  .object({
    /** Absent ⇒ `open`. */
    status: ShadowStaffFlagTriageStatusSchema.optional(),
    /** Server time staff last marked the flag complete; absent while open. */
    completedAt: FirestoreTimestampSchema.optional(),
    /** Uid of the staff member who marked it complete; absent while open. */
    completedBy: z.string().optional(),
    /** That staff member's display name when they marked it complete. */
    completedByName: z.string().optional(),
    /** Number of docs in the notes subcollection. Absent ⇒ 0. */
    noteCount: z.number().int().nonnegative().optional(),
    /** Server time of the newest note; absent when there are none. */
    lastNoteAt: FirestoreTimestampSchema.optional(),
    /** Server time of the last staff write. */
    updatedAt: FirestoreTimestampSchema,
    /** Uid of the staff member who last wrote the doc. */
    updatedBy: z.string(),
  })
  .loose();

/** A stored triage document. */
export type ShadowStaffFlagTriageDocument = z.infer<
  typeof ShadowStaffFlagTriageDocumentSchema
>;

/** One staff note on a flag. Append-only, like a report note. */
export const ShadowStaffFlagTriageNoteDocumentSchema = z
  .object({
    /** Uid of the staff member who wrote the note. */
    authorUid: z.string(),
    /** Their display name when they wrote it. */
    authorName: z.string(),
    /** Note body (plain text). */
    text: z.string(),
    /** Server time the note was written. */
    at: FirestoreTimestampSchema,
  })
  .loose();

/** A stored triage note. */
export type ShadowStaffFlagTriageNoteDocument = z.infer<
  typeof ShadowStaffFlagTriageNoteDocumentSchema
>;

/** Where a flag doc sits, as read from its path. */
export interface ShadowStaffFlagLocation {
  /** Course of the flagged section. */
  courseId: string;
  /** The flagged section. */
  sectionId: string;
  /** The section-week path segment. */
  sectionWeek: string;
  /** The flag doc's id (`{reviewerUid}` or `reviewee__{reviewerUid}`). */
  flagId: string;
}

/**
 * Reads a flag's location from its document path. The path is used rather
 * than the flag's fields because the rules do not pin a reviewer flag's
 * `sectionId` to its path.
 *
 * @param path - A `shadowStaffFlags` document path, as from `ref.path`.
 * @return The flag's location, or `null` when the path is not a flag path.
 * @throws Never.
 */
export function parseShadowStaffFlagPath(
  path: string,
): ShadowStaffFlagLocation | null {
  const parts = path.split('/');
  if (
    parts.length !== 8 ||
    parts[0] !== SECTION_ANALYSIS_COLLECTION ||
    parts[2] !== SECTION_ANALYSIS_SECTIONS_SUBCOLLECTION ||
    parts[4] !== SECTION_ANALYSIS_SECTION_WEEKS_SUBCOLLECTION ||
    parts[6] !== SECTION_ANALYSIS_SHADOW_STAFF_FLAGS_SUBCOLLECTION ||
    parts.some(part => part === '')
  ) {
    return null;
  }
  const [, courseId, , sectionId, , sectionWeek, , flagId] = parts;
  return {courseId, sectionId, sectionWeek, flagId};
}

/**
 * Triage doc id for a flag: `{sectionId}__{sectionWeek}__{flagId}`. The flag
 * id comes last because it may itself contain `__`.
 *
 * @param location - The flag's section, week and doc id.
 * @return The triage doc id.
 * @throws Never.
 */
export function getShadowStaffFlagTriageId({
  sectionId,
  sectionWeek,
  flagId,
}: Pick<
  ShadowStaffFlagLocation,
  'sectionId' | 'sectionWeek' | 'flagId'
>): string {
  return `${sectionId}__${sectionWeek}__${flagId}`;
}

/**
 * Triage collection path: `sectionShadow/{courseId}/staffFlagTriage`.
 *
 * @param params.courseId - The course.
 * @return The collection path.
 * @throws Never.
 */
export function getShadowStaffFlagTriageCollectionPath({
  courseId,
}: {
  courseId: CourseId;
}): string {
  return `${SECTION_SHADOW_COLLECTION}/${courseId}/${SECTION_SHADOW_STAFF_FLAG_TRIAGE_SUBCOLLECTION}`;
}

/**
 * Single triage document path.
 *
 * @param params.courseId - The course.
 * @param params.triageId - From {@link getShadowStaffFlagTriageId}.
 * @return The document path.
 * @throws Never.
 */
export function getShadowStaffFlagTriageDocumentPath({
  courseId,
  triageId,
}: {
  courseId: CourseId;
  triageId: string;
}): string {
  return `${getShadowStaffFlagTriageCollectionPath({courseId})}/${triageId}`;
}

/**
 * Notes collection path of one triage doc.
 *
 * @param params.courseId - The course.
 * @param params.triageId - From {@link getShadowStaffFlagTriageId}.
 * @return The collection path.
 * @throws Never.
 */
export function getShadowStaffFlagTriageNotesCollectionPath({
  courseId,
  triageId,
}: {
  courseId: CourseId;
  triageId: string;
}): string {
  return `${getShadowStaffFlagTriageDocumentPath({courseId, triageId})}/${SHADOW_STAFF_FLAG_TRIAGE_NOTES_SUBCOLLECTION}`;
}

/**
 * Whether a flag counts as complete. Marking complete holds only until the
 * flag's author writes the flag again: a flag written strictly after
 * `completedAtMs` is open again, and one written at or before it stays
 * complete. The stored status is left as it is, so no server-side reopen is
 * needed.
 *
 * @param params.status - The triage doc's status; `undefined` when there is no
 *   triage doc or no status (open).
 * @param params.completedAtMs - When it was marked complete; `null` when the
 *   doc has no `completedAt`, which leaves the stored status in force.
 * @param params.flagUpdatedAtMs - The flag's last write; `null` when the flag
 *   has no `updatedAt`, which leaves the stored status in force.
 * @return `true` when the flag is complete.
 * @throws Never.
 */
export function isShadowStaffFlagComplete({
  status,
  completedAtMs,
  flagUpdatedAtMs,
}: {
  status: ShadowStaffFlagTriageStatus | undefined;
  completedAtMs: number | null;
  flagUpdatedAtMs: number | null;
}): boolean {
  if (status !== 'complete') {
    return false;
  }
  if (completedAtMs === null || flagUpdatedAtMs === null) {
    return true;
  }
  return flagUpdatedAtMs <= completedAtMs;
}
